import type { PlazaSellerSale } from '@/net/plazaRoom';

export interface StallSale {
  id: string;
  kind: 'pet' | 'eggs';
  buyer_display_name: string;
  def_id: string | null;
  variant: string;
  quantity: number;
  price_crystals: number | null;
  net_crystals: number;
  commission_crystals: number | null;
  created_at: string;
}

export function salePosition(sale: Pick<StallSale, 'id' | 'created_at'>): string {
  return `${sale.created_at}|${sale.id}`;
}

export function saleNoticeFromEvent(event: PlazaSellerSale): StallSale | null {
  if (!event.saleId || !event.buyerDisplayName) return null;
  const gross = event.grossCrystals ?? null;
  return {
    id: event.saleId, kind: event.kind, buyer_display_name: event.buyerDisplayName,
    def_id: event.defId ?? null, variant: event.variant ?? 'normal', quantity: event.quantity,
    price_crystals: gross, net_crystals: event.netCrystals,
    // Older emitters do not tell us the price/commission. Do not invent a zero commission.
    commission_crystals: event.commissionCrystals ?? (gross === null ? null : gross - event.netCrystals),
    created_at: event.createdAt ?? new Date().toISOString(),
  };
}

interface NoticeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readSeenSaleIds(userId: string, storage?: NoticeStorage): string[] {
  try {
    const source = storage ?? (typeof window === 'undefined' ? null : window.localStorage);
    const parsed: unknown = JSON.parse(source?.getItem(`capibara:seen-stall-sales:${userId}`) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string'
      && id.length > 0 && id.length <= 128).slice(-500) : [];
  } catch { return []; }
}

export function writeSeenSaleIds(userId: string, ids: string[], storage?: NoticeStorage): void {
  try {
    const target = storage ?? (typeof window === 'undefined' ? null : window.localStorage);
    target?.setItem(`capibara:seen-stall-sales:${userId}`, JSON.stringify([...new Set(ids)].slice(-500)));
  } catch { /* Session deduplication still works when storage is disabled. */ }
}

export function mergeSaleNotices(queue: StallSale[], seen: string[], incoming: StallSale[]) {
  const known = new Set(seen);
  const fresh = incoming.filter((sale) => {
    if (known.has(sale.id)) return false;
    known.add(sale.id);
    return true;
  });
  return { queue: [...queue, ...fresh].slice(-10), seen: [...known].slice(-500) };
}
